import * as _ from "lodash";
import { type ReactNode, useCallback, useEffect, useRef } from "react";
import styled from "styled-components";

import CloseIcon from "@/assets/close.svg";
import type { Option } from "@/types/util-types";

import { types } from "./types";

const StyledCloseButton = styled.img`
    height: 9px;
    width: 9px;
`;

interface ToastrProps {
    /**
     * The toast notification context scope.
     */
    context?: string;
    /**
     * The close event handler.
     */
    onClose: (context?: string) => void;
    /**
     * Whether the toast is currently visible.
     */
    active: boolean;
    /**
     * The toast message text.
     */
    message?: string;
    /**
     * The notification type (e.g., "info", "error").
     */
    type?: string;
}

/**
 * Toast notification with auto-dismiss timer and close button.
 *
 * @param {ToastrProps} props - The component props.
 * @returns The rendered toast component.
 */
const Toastr = ({
    context,
    onClose,
    active,
    message,
    type,
}: ToastrProps): ReactNode => {
    const timerRef = useRef<Option<NodeJS.Timeout>>(null);
    const meta = _.find(types, (obj) => obj.type === type) ?? types[0];

    const onCloseClick = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        if (onClose) onClose(context);
    }, [context, onClose]);

    useEffect(() => {
        if (!active) return;
        timerRef.current = setTimeout(onCloseClick, meta.timeout);

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [active, meta.timeout, onCloseClick]);

    if (!active) return null;

    return (
        <div
            className={`toastr-wrapper ${active ? "opened" : "closed"}`}
            style={{ borderColor: meta.color }}
        >
            <div className="toastr-content">
                <div
                    className="toastr-icon"
                    style={{ backgroundColor: meta.color }}
                >
                    <i className={`fa fa-${meta.icon}`} />
                </div>
                <div className="content">
                    {active && (
                        <div className="toastr-close">
                            <span onClick={onCloseClick}>
                                <StyledCloseButton src={CloseIcon} />
                            </span>
                        </div>
                    )}
                    <div className="toastr-message">
                        <span>{message}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Toastr;
